import React, { useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BulkUploadPage = () => {
  const { language } = useLanguage();
  
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setResult(null);
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/admin/bulk/template`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'question_upload_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading template:', error);
      alert(language === 'tr' ? '❌ Şablon indirme hatası!' : '❌ Failed to download template!');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      alert(language === 'tr' ? 'Lütfen bir dosya seçin' : 'Please select a file');
      return;
    }

    setUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/admin/bulk/upload-questions`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setResult(response.data);
      setFile(null);
      
      // Reset file input
      document.getElementById('file-input').value = '';
    } catch (error) {
      console.error('Error uploading:', error);
      alert(language === 'tr' 
        ? `❌ Yükleme hatası: ${error.response?.data?.detail || error.message}` 
        : `❌ Upload failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <Navbar title={language === 'tr' ? 'Toplu Soru Yükleme' : 'Bulk Question Upload'} />
      
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {language === 'tr' ? '📤 Toplu Soru Yükleme' : '📤 Bulk Question Upload'}
          </h1>
          <p className="text-gray-600">
            {language === 'tr' 
              ? 'CSV dosyası ile birden fazla soru çiftini tek seferde yükleyin' 
              : 'Upload multiple question pairs at once via CSV file'}
          </p>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8 rounded-r-xl">
          <h3 className="font-bold text-blue-900 mb-3">
            {language === 'tr' ? '📋 Nasıl Kullanılır?' : '📋 How to Use?'}
          </h3>
          <ol className="list-decimal list-inside space-y-2 text-blue-800">
            <li>{language === 'tr' ? 'Şablon dosyasını indirin' : 'Download the template file'}</li>
            <li>{language === 'tr' ? 'Excel veya metin editörü ile açın' : 'Open with Excel or text editor'}</li>
            <li>{language === 'tr' ? 'Soru bilgilerini doldurun' : 'Fill in question information'}</li>
            <li>{language === 'tr' ? 'CSV olarak kaydedin (UTF-8)' : 'Save as CSV (UTF-8)'}</li>
            <li>{language === 'tr' ? 'Dosyayı buradan yükleyin' : 'Upload the file here'}</li>
          </ol>
        </div>

        {/* Template Download */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            {language === 'tr' ? '1️⃣ Şablon İndir' : '1️⃣ Download Template'}
          </h3>
          <p className="text-gray-600 mb-4">
            {language === 'tr' 
              ? 'Örnek verilerle dolu CSV şablonunu indirin' 
              : 'Download CSV template with example data'}
          </p>
          <button
            onClick={handleDownloadTemplate}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition"
          >
            ⬇️ {language === 'tr' ? 'Şablonu İndir' : 'Download Template'}
          </button>
        </div>

        {/* File Upload */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            {language === 'tr' ? '2️⃣ Dosya Yükle' : '2️⃣ Upload File'}
          </h3>
          
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {language === 'tr' ? 'CSV Dosyası Seçin' : 'Select CSV File'}
            </label>
            <input
              id="file-input"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition"
            />
            {file && (
              <p className="mt-2 text-sm text-green-600">
                ✓ {file.name} ({(file.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>

          <button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading 
              ? (language === 'tr' ? '⏳ Yükleniyor...' : '⏳ Uploading...') 
              : (language === 'tr' ? '🚀 Yükle' : '🚀 Upload')
            }
          </button>
        </div>

        {/* Results */}
        {result && (
          <div className={`rounded-xl shadow-lg p-6 ${
            result.errors && result.errors.length > 0 
              ? 'bg-yellow-50 border-l-4 border-yellow-500' 
              : 'bg-green-50 border-l-4 border-green-500'
          }`}>
            <h3 className="text-xl font-bold mb-4">
              {language === 'tr' ? '✅ Yükleme Sonucu' : '✅ Upload Result'}
            </h3>
            
            <div className="space-y-2 mb-4">
              <p className="text-lg">
                <strong>{language === 'tr' ? 'Yüklenen:' : 'Uploaded:'}</strong>{' '}
                <span className="text-green-600 font-bold">{result.uploaded}</span> {language === 'tr' ? 'soru çifti' : 'question pairs'}
              </p>
              {result.errors && result.errors.length > 0 && (
                <p className="text-lg">
                  <strong>{language === 'tr' ? 'Hatalar:' : 'Errors:'}</strong>{' '}
                  <span className="text-red-600 font-bold">{result.errors.length}</span>
                </p>
              )}
            </div>

            {result.errors && result.errors.length > 0 && (
              <div className="mt-4">
                <h4 className="font-semibold text-yellow-900 mb-2">
                  {language === 'tr' ? 'Hata Detayları:' : 'Error Details:'}
                </h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-yellow-800">
                  {result.errors.map((err, idx) => (
                    <li key={idx}>
                      {language === 'tr' ? 'Satır' : 'Line'} {err.line}: {err.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* CSV Format Guide */}
        <div className="bg-gray-50 rounded-xl p-6 mt-8">
          <h3 className="text-lg font-bold text-gray-900 mb-3">
            {language === 'tr' ? '📝 CSV Format Rehberi' : '📝 CSV Format Guide'}
          </h3>
          <div className="text-sm text-gray-700 space-y-2">
            <p><strong>subject:</strong> Physics, Chemistry, Biology, Mathematics</p>
            <p><strong>topic_tags:</strong> {language === 'tr' ? 'Konular (| ile ayırın)' : 'Topics (separate with |)'}</p>
            <p><strong>difficulty:</strong> easy, medium, hard</p>
            <p><strong>tyt_options:</strong> A) Şık 1|B) Şık 2|C) Şık 3|D) Şık 4</p>
            <p><strong>tyt_correct:</strong> A, B, C, veya D</p>
            <p><strong>ap_rubric_points:</strong> {language === 'tr' ? 'Puanlama kriterleri (| ile ayırın)' : 'Rubric points (separate with |)'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadPage;
